import { Link, useNavigate } from 'react-router';
import {
  BarChart3,
  Building2,
  CalendarDays,
  Handshake,
  Lock,
  Phone,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { formatEuro, formatInteger, formatTime } from '@brimo/shared';
import { clsx } from '../../ds/clsx';
import { Banner, Button, Card, Eyebrow, Icon, Metric, Pill, Skeleton, TextLink } from '../../ds/components';
import { useIsMobile } from '../../ds/hooks';
import { errorMessage } from '../../lib/api';
import { usePermissions } from '../../lib/session';
import { useHome, type HomeArea, type HomeAreaKey, type HomeData } from './api';
import {
  appointmentStatusPills,
  areaTitles,
  greetingEyebrow,
  greetingSentence,
  greetingTitle,
  leadsDelta,
  lifecycleChipVariants,
  nextAppointmentText,
  pipelineDelta,
} from './text';
import styles from './HomePage.module.css';

const areaIcons: Record<HomeAreaKey, LucideIcon> = {
  contacts: Users,
  properties: Building2,
  deals: Handshake,
  calendar: CalendarDays,
  dialer: Phone,
  controlling: BarChart3,
};

function count(value: number | string): string {
  return typeof value === 'number' ? formatInteger(value) : value;
}

function Greeting({ data, mobile, now }: { data: HomeData; mobile: boolean; now: Date }) {
  const navigate = useNavigate();
  const permissions = usePermissions();
  const canCreateContact = permissions.has('contacts.create');
  return (
    <header className={styles.greeting}>
      <div className={styles.greetingText}>
        <Eyebrow size={11}>{greetingEyebrow(data.greeting.organizationName, data.greeting.today)}</Eyebrow>
        <h1 className={styles.title}>{greetingTitle(data.greeting.firstName, now)}</h1>
        <p className={styles.sentence}>{greetingSentence(data.greeting, mobile)}</p>
      </div>
      <div className={styles.actions}>
        <Button
          variant="secondary"
          className={styles.secondaryAction}
          onClick={() => void navigate('/calendar/new', { state: { from: '/home' } })}
        >
          Termin buchen
        </Button>
        {canCreateContact ? (
          <Button block={mobile} onClick={() => void navigate('/contacts/new')}>
            Kontakt anlegen
          </Button>
        ) : null}
      </div>
    </header>
  );
}

function Kpis({ data }: { data: HomeData }) {
  const { kpis } = data;
  const leads = leadsDelta(kpis.newLeads.deltaVsPreviousWeek);
  return (
    <section className={styles.kpis} aria-label="Kennzahlen">
      <Metric
        variant="dark"
        label="Termine heute"
        value={formatInteger(kpis.appointmentsToday.count)}
        delta={nextAppointmentText(kpis.appointmentsToday.next)}
      />
      <Metric
        label="Offene Aufgaben"
        value={formatInteger(kpis.openTasks.count)}
        delta={`${formatInteger(kpis.openTasks.overdue)} überfällig`}
        deltaTone={kpis.openTasks.overdue > 0 ? 'negative' : 'neutral'}
      />
      <Metric
        label="Neue Leads · 7 Tage"
        value={formatInteger(kpis.newLeads.count)}
        delta={leads.text}
        deltaTone={leads.tone}
      />
      {kpis.pipeline === null ? null : (
        <Metric
          label="Pipeline-Volumen"
          value={
            kpis.pipeline.volumeCents === null ? 'Summen ausgeblendet' : formatEuro(kpis.pipeline.volumeCents)
          }
          delta={pipelineDelta(kpis.pipeline.openDeals, kpis.pipeline.reserved)}
        />
      )}
    </section>
  );
}

function AreaTile({ area }: { area: HomeArea }) {
  const locked = area.access === 'locked';
  return (
    <Link
      to={area.href}
      className={clsx(styles.area, locked && styles.areaLocked)}
      aria-label={`${areaTitles[area.key]}: ${count(area.count)}, ${area.line}`}
    >
      <span className={styles.areaIcon}>
        <Icon icon={locked ? Lock : areaIcons[area.key]} size={20} />
      </span>
      <span className={styles.areaText}>
        <span className={styles.areaTitle}>{areaTitles[area.key]}</span>
        <span className={styles.areaLine}>{area.line}</span>
      </span>
      <span className={styles.areaCount}>{count(area.count)}</span>
    </Link>
  );
}

function Areas({ areas }: { areas: HomeArea[] }) {
  return (
    <section className={styles.section} aria-labelledby="home-areas">
      <Eyebrow size={11} id="home-areas" className={styles.sectionEyebrow}>
        Bereiche
      </Eyebrow>
      <div className={styles.areas}>
        {areas.map((area) => (
          <AreaTile key={area.key} area={area} />
        ))}
      </div>
    </section>
  );
}

function Pipeline({ pipeline }: { pipeline: NonNullable<HomeData['pipeline']> }) {
  return (
    <section className={styles.section} aria-labelledby="home-pipeline">
      <div className={styles.sectionHead}>
        <Eyebrow size={11} id="home-pipeline">
          {`Pipeline · ${pipeline.name}`}
        </Eyebrow>
        <TextLink to="/deals" size="small">
          Alle Deals
        </TextLink>
      </div>
      <ol className={styles.stages}>
        {pipeline.stages.map((stage) => (
          <li key={stage.key} className={styles.stage}>
            <span className={styles.stageCount}>{formatInteger(stage.count)}</span>
            <span className={styles.stageLabel}>{stage.label}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}

function Today({ items }: { items: HomeData['today'] }) {
  return (
    <Card role="region" aria-labelledby="home-today">
      <div className={styles.cardHead}>
        <h2 id="home-today" className={styles.cardTitle}>
          Heute
        </h2>
        <TextLink to="/calendar" size="small">
          Kalender öffnen
        </TextLink>
      </div>
      {items.length === 0 ? (
        <p className={styles.empty}>Heute keine Termine.</p>
      ) : (
        <ul className={styles.appointments}>
          {items.map((item) => {
            const pill = appointmentStatusPills[item.status];
            return (
              <li key={item.id}>
                <Link to={`/calendar/appointments/${item.id}`} className={styles.appointment}>
                  <span className={styles.appointmentTime}>{formatTime(item.at)}</span>
                  <span className={styles.appointmentText}>
                    <span className={styles.appointmentName}>{item.contactName}</span>
                    <span className={styles.appointmentLine}>{item.line}</span>
                  </span>
                  <Pill variant={pill.variant}>{pill.label}</Pill>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

function Lifecycle({ groups }: { groups: HomeData['contactsByLifecycle'] }) {
  return (
    <Card role="region" aria-labelledby="home-lifecycle">
      <div className={styles.cardHead}>
        <h2 id="home-lifecycle" className={styles.cardTitle}>
          Meine Kunden nach Status
        </h2>
        <TextLink to="/contacts" size="small">
          Alle Kontakte
        </TextLink>
      </div>
      <ul className={styles.chips}>
        {groups.map((group) => (
          <li key={group.key}>
            <Pill variant={lifecycleChipVariants[group.key]} size="large">
              <span>{group.label}</span>
              <span className={styles.chipCount}>{formatInteger(group.count)}</span>
            </Pill>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function HomeSkeleton() {
  return (
    <div className={styles.page} aria-busy="true" aria-label="Wird geladen">
      <div className={styles.greeting}>
        <div className={styles.greetingText}>
          <Skeleton width={220} height={10} />
          <Skeleton width={360} height={38} />
          <Skeleton width={420} />
        </div>
      </div>
      <div className={styles.kpis}>
        {Array.from({ length: 4 }, (_, index) => (
          <Skeleton key={index} variant="block" height={112} />
        ))}
      </div>
      <div className={styles.section}>
        <div className={styles.areas}>
          {Array.from({ length: 6 }, (_, index) => (
            <Skeleton key={index} variant="block" height={92} />
          ))}
        </div>
      </div>
      <div className={styles.columns}>
        <Skeleton variant="block" height={220} />
        <Skeleton variant="block" height={220} />
      </div>
    </div>
  );
}

/** The content of the start page; split from the query so tests can render it with fixed data. */
export function HomeView({ data, mobile, now }: { data: HomeData; mobile: boolean; now: Date }) {
  return (
    <div className={styles.page}>
      <Greeting data={data} mobile={mobile} now={now} />
      <Kpis data={data} />
      {data.areas.length > 0 ? <Areas areas={data.areas} /> : null}
      {data.pipeline !== null && !mobile ? <Pipeline pipeline={data.pipeline} /> : null}
      <div className={styles.columns}>
        <Today items={data.today} />
        {data.contactsByLifecycle.length > 0 && !mobile ? (
          <Lifecycle groups={data.contactsByLifecycle} />
        ) : null}
      </div>
    </div>
  );
}

/** Start page `/home` (Nico feedback 28.10.2026, design approved 29.10.2026; D-327…D-331). */
export function HomePage() {
  const home = useHome();
  const mobile = useIsMobile();
  if (home.isPending) return <HomeSkeleton />;
  if (home.isError) {
    return (
      <div className={styles.page}>
        <Banner tone="danger">{errorMessage(home.error)}</Banner>
      </div>
    );
  }
  return <HomeView data={home.data} mobile={mobile} now={new Date()} />;
}
